import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '../ui';
import PremiumName from '../ui/PremiumName';
import { useUserData } from '../../hooks/useUserData';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { toggleSaveService } from '../../services/savedService';
import { formatPostDate, formatFullDate } from '../../utils/dateFormatter';

/**
 * ServiceCard - Tarjeta especializada para servicios
 * Props opcionales: currentUserId, savedServiceIds, onToggleSaveService
 */
const ServiceCard = ({ service, onClick, currentUserId: currentUserIdProp, savedServiceIds: savedServiceIdsProp, onToggleSaveService }) => {
  const { userData } = useAuth();
  const { showToast } = useToast();
  const currentUserId = currentUserIdProp ?? userData?.id;
  const savedServiceIds = savedServiceIdsProp ?? (userData?.savedServices || []);
  const isSaved = currentUserId && Array.isArray(savedServiceIds) && savedServiceIds.includes(service.id);

  const handleToggleSave = async (e) => {
    e?.stopPropagation();
    if (!currentUserId) return;
    const result = await toggleSaveService(currentUserId, service.id);
    if (result.success && result.saved) {
      showToast('Servicio guardado');
    }
    if (onToggleSaveService && result.success && !result.saved) {
      onToggleSaveService(service.id);
    }
  };

  const { userData: authorData } = useUserData(service.userId);
  const authorName = authorData?.displayName || service.userName || 'Usuario';
  const authorIsPremium = authorData?.isPremium;
  const authorPremiumColor = authorData?.premiumNameColor || null;
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Las imágenes pueden venir como strings directos o como objetos con url
  const media = service.media || service.imageUrls || [];
  const firstMedia = media.length > 0 ? media[0] : null;
  const firstMediaUrl = typeof firstMedia === 'string' ? firstMedia : firstMedia?.url;
  const hasMultipleMedia = media.length > 1;

  return (
    <div 
      className="group bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-slate-700 cursor-pointer h-full flex flex-col"
      onClick={() => onClick(service)}
    >
      {/* Imagen del servicio */}
      <div className="relative overflow-hidden bg-gray-100 dark:bg-slate-700 aspect-video">
        {firstMediaUrl && !imageError ? (
          <>
            {/* Skeleton mientras carga */}
            {!imageLoaded && (
              <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-slate-600" />
            )}
            
            <img
              src={firstMediaUrl}
              alt={service.caption || 'Servicio'}
              className={`w-full h-full object-cover transition-all duration-500 ${
                imageLoaded ? 'opacity-100 group-hover:scale-110' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              loading="lazy"
            />

            {/* Botón guardar - esquina superior derecha */}
            {currentUserId && (
              <button
                type="button"
                onClick={handleToggleSave}
                className="absolute top-3 right-3 p-1.5 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white transition-all z-10"
                title={isSaved ? 'Quitar de guardados' : 'Guardar'}
                aria-label={isSaved ? 'Quitar de guardados' : 'Guardar'}
              >
                {isSaved ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5 2h14a1 1 0 011 1v19.143a1 1 0 01-1.581.814L12 18.499l-6.419 4.458A1 1 0 014 22.143V3a1 1 0 011-1z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-5-7 5V5z" />
                  </svg>
                )}
              </button>
            )}

            {/* Badge de múltiples imágenes */}
            {hasMultipleMedia && (
              <div className="absolute top-3 right-12 px-2 py-1 bg-black/70 backdrop-blur-sm text-white text-xs font-medium rounded-md flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {media.length}
              </div>
            )}

            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        {/* Badge de Servicio */}
        <div className="absolute top-3 left-3 px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-full shadow-lg flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Servicio
        </div>
      </div>

      {/* Contenido del servicio */}
      <div className="p-4 flex-1 flex flex-col">
        {/* Título/Caption */}
        <h3 className="font-semibold text-gray-900 dark:text-white text-base line-clamp-2 mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
          {service.caption || 'Servicio sin título'}
        </h3>

        {/* Descripción */}
        {service.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
            {service.description}
          </p>
        )}

        {/* Categoría/Tags */}
        {service.category && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 text-xs rounded-md">
              {service.category}
            </span>
          </div>
        )}

        {/* Métodos de pago del vendedor (compacto) */}
        {(() => {
          const pm = authorData?.paymentMethods || {};
          const hasCvu = pm.cvu?.active && (pm.cvu?.alias?.trim() || pm.cvu?.cvu?.trim());
          const hasPaypal = pm.paypal?.active && pm.paypal?.email?.trim();
          const hasCrypto = pm.crypto?.active && pm.crypto?.address?.trim();
          const hasAny = hasCvu || hasPaypal || hasCrypto;
          if (!hasAny) return null;
          const labels = [];
          if (hasCvu) labels.push('Alias/CVU');
          if (hasPaypal) labels.push('PayPal');
          if (hasCrypto) labels.push('Cripto');
          return (
            <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2h-2m-4-1V7a2 2 0 012-2h2a2 2 0 012 2v1" />
              </svg>
              <span>Pago: {labels.join(' · ')}</span>
            </div>
          );
        })()}

        {/* Footer con precio y autor */}
        <div className="mt-auto pt-3 border-t border-gray-100 dark:border-slate-700">
          <div className="flex items-center justify-between mb-3">
            {/* Precio */}
            {service.servicePrice > 0 ? (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Desde</span>
                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  ${service.servicePrice}
                </span>
              </div>
            ) : (
              <div className="flex flex-col">
                <span className="text-lg font-bold text-green-600 dark:text-green-400">
                  Gratis
                </span>
              </div>
            )}

            {/* Stats del servicio */}
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              {service.orders > 0 && (
                <div className="flex items-center gap-1" title="Pedidos completados">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{service.orders} pedidos</span>
                </div>
              )}
              {service.deliveryTime && (
                <div className="flex items-center gap-1" title="Tiempo de entrega">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{service.deliveryTime}</span>
                </div>
              )}
              {service.prices && Array.isArray(service.prices) && service.prices.length > 1 && (
                <div className="flex items-center gap-1" title="Múltiples paquetes disponibles">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <span>{service.prices.length} paquetes</span>
                </div>
              )}
            </div>
          </div>

          {/* Autor */}
          <Link
            to={`/profile/${service.userId}`}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            <Avatar
              src={authorData?.photoURL || service.userPhotoURL}
              alt={authorName}
              fallbackText={authorName}
              size="sm"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <PremiumName
                  name={authorName}
                  isPremium={authorIsPremium}
                  premiumColor={authorPremiumColor}
                  nameClassName="text-sm font-medium text-gray-900 dark:text-white"
                  iconSize="xs"
                />
                {service.createdAt && (
                  <span
                    className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
                    title={formatFullDate(service.createdAt)}
                  >
                    • {formatPostDate(service.createdAt)}
                  </span>
                )}
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;
